import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useAuthStore } from "./store/authStore";
import Layout from "./components/Layout";
import Login from "./screens/Login";
import Overview from "./screens/Overview";
import Activity from "./screens/Activity";
import ResourceList from "./screens/ResourceList";
import ResourceDetail from "./screens/ResourceDetail";
import ResourceNew from "./screens/ResourceNew";
import Settings from "./screens/Settings";

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-700 border-t-accent" />
    </div>
  );
}

// A signed-in non-superadmin gets a dead end, not a redirect loop back to a
// login they have already passed.
function Forbidden() {
  const signOut = useAuthStore((s) => s.signOut);
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="panel max-w-sm p-6 text-center">
        <ShieldOff className="mx-auto mb-3 h-6 w-6 text-red-400" />
        <h1 className="mb-1 font-semibold text-white">Ingen behörighet</h1>
        <p className="mb-4 text-sm text-ink-400">
          Kontot är inloggat men saknar superadmin-behörighet.
        </p>
        <button onClick={() => signOut().then(() => (window.location.href = "/login"))} className="btn-ghost w-full justify-center">
          Logga ut
        </button>
      </div>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitializing, isForbidden } = useAuthStore();
  if (isInitializing) return <Splash />;
  if (isForbidden) return <Forbidden />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  useEffect(() => { initialize(); }, [initialize]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Protected><Layout /></Protected>}>
          <Route index element={<Overview />} />
          <Route path="activity" element={<Activity />} />
          <Route path="r/:key" element={<ResourceList />} />
          {/* Static segment declared first so /r/garages/new is never read as an id. */}
          <Route path="r/:key/new" element={<ResourceNew />} />
          <Route path="r/:key/:id" element={<ResourceDetail />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
